import { SensorMetadataDialog } from "./SensorMetadataDialog";
import {
  orderTopologyDevices,
  TopologyDeviceOrder,
} from "./topologyDeviceOrder";
import { sorted } from "helpers";
import { alignGraphSlots } from "./topologyLayout";
import { SENSOR_TYPE_LABEL_KEYS } from "./sensorTypeLabels";
import { alpha, Box, Button, Typography, useTheme } from "@mui/material";
import { SensorTopologyNode } from "./SensorTopologyNode";
import { positionColours } from "./PositionBadge";
import { useState } from "react";
import { useTenantTranslation } from "hooks/formatters";
import type {
  SensorTopology,
  ScrapEyeSensorRead,
  ScrapEyeRecordingPosition,
} from "src/store/api/generatedApi";
import {
  activeConnections,
  activeInstallations,
  TopologyEdit,
  TopologySelection,
} from "./topologyEditing";

interface SensorTopologyGraphProps {
  topology: SensorTopology;
  positions: ScrapEyeRecordingPosition[];
  selection: TopologySelection | null;
  onSelect: (selection: TopologySelection) => void;
  onEdit: (edit: TopologyEdit) => void;
  search: string;
  showInactive: boolean;
  busy: boolean;
  deviceOrder: TopologyDeviceOrder;
}

const ROW_Y = { device: 48, sensor: 218, position: 410 };
const NODE_WIDTH = 180;
const NODE_HEIGHT = 60;
const SENSOR_HEIGHT = 100;
const COLUMN_WIDTH = 204;
const GRAPH_HEIGHT = 494;
const nodeX = (index: number) => 20 + index * COLUMN_WIDTH;

export const SensorTopologyGraph = ({
  topology,
  positions,
  selection,
  onSelect,
  onEdit,
  search,
  showInactive,
  busy,
  deviceOrder,
}: SensorTopologyGraphProps) => {
  const [editingSensor, setEditingSensor] = useState<ScrapEyeSensorRead | null>(
    null
  );
  const { t } = useTenantTranslation();
  const theme = useTheme();
  const colours = positionColours(positions, theme);
  const [drag, setDrag] = useState<{
    sensorId: number;
    connectionId: number | null;
  } | null>(null);
  const connections = activeConnections(topology);
  const installations = activeInstallations(topology);
  const matches = (name: string) =>
    name.toLocaleLowerCase().includes(search.toLocaleLowerCase());
  const matchingDeviceIds = new Set(
    topology.devices
      .filter((device) => matches(device.name))
      .map((device) => device.id)
  );
  const matchingPositionIds = new Set(
    positions
      .filter((position) =>
        matches(`${position.location_name} ${position.name}`)
      )
      .map((position) => position.id)
  );
  const filteredSensors = topology.sensors.filter((sensor) => {
    const sensorConnections = connections.filter(
      (connection) => connection.sensor_id === sensor.id
    );
    const sensorInstallations = installations.filter(
      (installation) => installation.sensor_id === sensor.id
    );
    return (
      (showInactive ||
        sensorConnections.length > 0 ||
        sensorInstallations.length > 0) &&
      (matches(`${sensor.name} ${sensor.serial_number ?? ""}`) ||
        sensorConnections.some((connection) =>
          matchingDeviceIds.has(connection.device_id)
        ) ||
        sensorInstallations.some((installation) =>
          matchingPositionIds.has(installation.recording_position_id)
        ))
    );
  });
  const orderedDevices = orderTopologyDevices(topology, deviceOrder);
  const deviceRank = new Map(
    orderedDevices.map((device, index) => [device.id, index])
  );
  const sensors = sorted(filteredSensors, (sensor) => [
    Math.min(
      topology.devices.length,
      ...connections
        .filter((connection) => connection.sensor_id === sensor.id)
        .map(
          (connection) =>
            deviceRank.get(connection.device_id) ?? topology.devices.length
        )
    ),
    sensor.id,
  ]);
  const sensorIds = new Set(sensors.map((sensor) => sensor.id));
  const devices = orderedDevices.filter(
    (device) =>
      connections.some(
        (connection) =>
          connection.device_id === device.id &&
          sensorIds.has(connection.sensor_id)
      ) ||
      (showInactive && matches(device.name))
  );
  const displayedPositions = positions.filter(
    (position) =>
      installations.some(
        (installation) =>
          installation.recording_position_id === position.id &&
          sensorIds.has(installation.sensor_id)
      ) ||
      (showInactive && matches(`${position.location_name} ${position.name}`))
  );
  const sensorIndex = new Map(
    sensors.map((sensor, index) => [sensor.id, index])
  );
  const deviceIndex = alignGraphSlots(
    devices.map((device) => ({
      id: device.id,
      neighbours: connections
        .filter((connection) => connection.device_id === device.id)
        .flatMap((connection) => {
          const row = sensorIndex.get(connection.sensor_id);
          return row == null ? [] : [row];
        }),
    })),
    sensors.length,
    "input"
  );
  const positionIndex = alignGraphSlots(
    displayedPositions.map((position) => ({
      id: position.id,
      neighbours: installations
        .filter(
          (installation) => installation.recording_position_id === position.id
        )
        .flatMap((installation) => {
          const row = sensorIndex.get(installation.sensor_id);
          return row == null ? [] : [row];
        }),
    })),
    sensors.length,
    "neighbours"
  );
  const width = Math.max(
    720,
    40 +
      (1 +
        Math.max(
          sensors.length - 1,
          ...deviceIndex.values(),
          ...positionIndex.values()
        )) *
        COLUMN_WIDTH
  );
  const selectedSensor = (sensorId: number) =>
    selection == null ||
    (selection.kind === "sensor" && selection.id === sensorId) ||
    (selection.kind === "device" &&
      connections.some(
        (connection) =>
          connection.sensor_id === sensorId &&
          connection.device_id === selection.id
      )) ||
    (selection.kind === "position" &&
      installations.some(
        (installation) =>
          installation.sensor_id === sensorId &&
          installation.recording_position_id === selection.id
      ));
  const drop = (target: TopologySelection) => {
    if (drag == null || busy) return;
    const connection = topology.connections.find(
      (candidate) => candidate.id === drag.connectionId
    );
    if (target.kind === "device")
      onEdit(
        connection == null
          ? { kind: "connect", sensorId: drag.sensorId, deviceId: target.id }
          : { kind: "reconnect", connection, deviceId: target.id }
      );
    if (target.kind === "position" && connection == null) {
      const sensorInstallations = installations.filter(
        (installation) => installation.sensor_id === drag.sensorId
      );
      const installation =
        sensorInstallations.length === 1 ? sensorInstallations[0] : null;
      onEdit(
        installation == null
          ? { kind: "install", sensorId: drag.sensorId, positionId: target.id }
          : { kind: "move", installation, positionId: target.id }
      );
    }
    setDrag(null);
  };
  const node = (
    target: TopologySelection,
    name: string,
    subtitle: string,
    index: number,
    active: boolean
  ) => (
    <SensorTopologyNode
      key={`${target.kind}-${target.id}`}
      target={target}
      name={name}
      subtitle={subtitle}
      left={nodeX(index)}
      top={ROW_Y[target.kind]}
      width={NODE_WIDTH}
      height={target.kind === "sensor" ? SENSOR_HEIGHT : NODE_HEIGHT}
      selected={selection?.kind === target.kind && selection.id === target.id}
      active={active}
      busy={busy}
      positionColours={colours}
      installations={
        target.kind === "sensor"
          ? installations.filter(
              (installation) => installation.sensor_id === target.id
            )
          : []
      }
      onSelect={onSelect}
      onEdit={onEdit}
      onEditMetadata={(sensorId) => {
        setEditingSensor(
          topology.sensors.find((sensor) => sensor.id === sensorId) ?? null
        );
      }}
      onDrop={drop}
      dragging={drag != null}
      onDragStart={(sensorId) => {
        setDrag({ sensorId, connectionId: null });
      }}
      onDragEnd={() => {
        setDrag(null);
      }}
    />
  );
  return (
    <Box
      sx={{
        overflow: "auto",
        height: GRAPH_HEIGHT,
        border: 1,
        borderColor: "divider",
        bgcolor: "background.default",
      }}
    >
      <Box
        sx={{
          position: "relative",
          width,
          minWidth: "100%",
          height: GRAPH_HEIGHT - 16,
        }}
      >
        {(
          [
            ["device", t("installationDeviceLabel")],
            ["sensor", t("sensorNameLabel")],
            ["position", t("recordingPositionsTitle")],
          ] as const
        ).map(([kind, label]) => (
          <Box
            key={kind}
            sx={{
              position: "absolute",
              top: ROW_Y[kind] - 32,
              width: "100%",
              borderBottom: 1,
              borderColor: "divider",
              bgcolor: "background.paper",
            }}
          >
            <Typography
              variant="subtitle2"
              sx={{
                position: "sticky",
                left: 16,
                display: "inline-block",
                py: 0.5,
              }}
            >
              {label}
            </Typography>
          </Box>
        ))}
        <svg
          width={width}
          height={GRAPH_HEIGHT}
          style={{ position: "absolute", top: 0, left: 0 }}
          aria-hidden="true"
        >
          {connections.map((connection) => {
            const from = deviceIndex.get(connection.device_id);
            const to = sensorIndex.get(connection.sensor_id);
            if (from == null || to == null) return null;
            const fromX = nodeX(from) + NODE_WIDTH / 2;
            const toX = nodeX(to) + NODE_WIDTH / 2;
            return (
              <path
                key={connection.id}
                d={`M ${fromX} ${ROW_Y.device + NODE_HEIGHT} C ${fromX} 164, ${toX} 164, ${toX} ${ROW_Y.sensor}`}
                fill="none"
                stroke={
                  selection != null && selectedSensor(connection.sensor_id)
                    ? theme.palette.primary.main
                    : theme.palette.divider
                }
                strokeWidth={selectedSensor(connection.sensor_id) ? 2 : 1}
              />
            );
          })}
          {installations.map((installation) => {
            const from = sensorIndex.get(installation.sensor_id);
            const to = positionIndex.get(installation.recording_position_id);
            if (from == null || to == null) return null;
            const fromX = nodeX(from) + NODE_WIDTH / 2;
            const toX = nodeX(to) + NODE_WIDTH / 2;
            return (
              <path
                key={installation.id}
                d={`M ${fromX} ${ROW_Y.sensor + SENSOR_HEIGHT} C ${fromX} 364, ${toX} 364, ${toX} ${ROW_Y.position}`}
                fill="none"
                stroke={alpha(
                  colours.get(installation.recording_position_id) ??
                    theme.palette.text.secondary,
                  selectedSensor(installation.sensor_id) ? 0.85 : 0.3
                )}
                strokeWidth={2}
                strokeDasharray="5 3"
              />
            );
          })}
        </svg>
        {devices.map((device, index) =>
          node(
            { kind: "device", id: device.id },
            device.name,
            t(
              connections.some(
                (connection) => connection.device_id === device.id
              )
                ? "topologyConnected"
                : "topologyInactive"
            ),
            deviceIndex.get(device.id) ?? index,
            connections.some((connection) => connection.device_id === device.id)
          )
        )}
        {sensors.map((sensor, index) =>
          node(
            { kind: "sensor", id: sensor.id },
            sensor.name,
            sensor.serial_number ??
              t(SENSOR_TYPE_LABEL_KEYS[sensor.sensor_type]),
            index,
            connections.some(
              (connection) => connection.sensor_id === sensor.id
            ) ||
              installations.some(
                (installation) => installation.sensor_id === sensor.id
              )
          )
        )}
        {displayedPositions.map((position, index) =>
          node(
            { kind: "position", id: position.id },
            position.name,
            position.location_name,
            positionIndex.get(position.id) ?? index,
            installations.some(
              (installation) =>
                installation.recording_position_id === position.id
            )
          )
        )}
        {connections.map((connection) => {
          const from = deviceIndex.get(connection.device_id);
          const to = sensorIndex.get(connection.sensor_id);
          if (
            from == null ||
            to == null ||
            selection == null ||
            !selectedSensor(connection.sensor_id)
          )
            return null;
          return (
            <Button
              key={connection.id}
              size="small"
              draggable={!busy}
              disabled={busy}
              aria-label={t("topologyReconnectLabel", {
                sensor: connection.sensor_name,
                device: connection.device_name,
              })}
              onDragStart={(event) => {
                event.dataTransfer.setData("text/plain", String(connection.id));
                setDrag({
                  sensorId: connection.sensor_id,
                  connectionId: connection.id,
                });
              }}
              onDragEnd={() => {
                setDrag(null);
              }}
              onClick={() => {
                onEdit({ kind: "reconnect", connection, deviceId: null });
              }}
              sx={{
                position: "absolute",
                left: (nodeX(from) + nodeX(to)) / 2 + NODE_WIDTH / 2,
                top: 152,
                transform: "translateX(-50%)",
                minWidth: 35,
                maxWidth: 100,
                typography: "caption",
                p: 0.25,
                bgcolor: "background.paper",
              }}
            >
              {connection.device_sensor_id ?? t("topologyLink")}
            </Button>
          );
        })}
        {sensors.length === 0 && (
          <Typography
            color="text.secondary"
            sx={{
              position: "absolute",
              top: ROW_Y.sensor + 12,
              left: 20,
              width: 300,
            }}
          >
            {t("topologyNoActiveSensors")}
          </Typography>
        )}
      </Box>
      {editingSensor != null && (
        <SensorMetadataDialog
          sensor={editingSensor}
          onClose={() => {
            setEditingSensor(null);
          }}
        />
      )}
    </Box>
  );
};
